import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Layout from './components/Layout';
import LandingLayout from './components/LandingLayout';
import ProtectedRoute from './components/ProtectedRoute';
import HomePage from './pages/HomePage';
import YouPage from './pages/YouPage';
import LogPage from './pages/LogPage';
import EntriesPage from './pages/EntriesPage';
import DataPage from './pages/DataPage';
import InsightsPage from './pages/InsightsPage';
import MePage from './pages/MePage';
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import GoogleCallbackPage from './pages/GoogleCallbackPage';
import FitbitCallbackPage from './pages/FitbitCallbackPage';
import DeviceCallbackPage from './pages/DeviceCallbackPage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import ArticlesListPage from './pages/ArticlesListPage';
import ArticleDetailPage from './pages/ArticleDetailPage';
import FeaturePage from './pages/FeaturePage';
import FitbitAutoSync from './components/fitbit/FitbitAutoSync';
import API_CONFIG from './config/api';

// Component to redirect to backend admin panel
function AdminRedirect() {
  const { user, isAuthenticated, loading } = useAuth();

  useEffect(() => {
    if (loading) return; // Still loading auth state

    if (!isAuthenticated()) {
      // User not authenticated, redirect to login
      window.location.href = '/login?redirect=/admin';
      return;
    }

    if (!user?.is_admin) {
      // User authenticated but not admin
      alert('Access denied. Admin privileges required.');
      window.location.href = '/';
      return;
    }

    // User is authenticated and has admin privileges, create admin session and redirect
    const createAdminSession = async () => {
      try {
        const sessionResponse = await fetch(`${API_CONFIG.BASE_URL}/api/auth/admin/session`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!sessionResponse.ok) {
          throw new Error('Failed to create admin session');
        }

        // Redirect to SQLAdmin interface
        window.location.href = `${API_CONFIG.BASE_URL}/k9x2p7m4q1`;
      } catch (err) {
        console.error('Failed to create admin session:', err);
        alert('Failed to access admin panel. Please try again.');
        window.location.href = '/';
      }
    };

    createAdminSession();
  }, [user, isAuthenticated, loading]);

  if (loading) {
    return (
      <div className="text-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-600 mx-auto mb-4"></div>
        <p className="text-gray-600 dark:text-gray-400">Verifying admin access...</p>
      </div>
    );
  }

  return null;
}

// Redirect authenticated users from landing to /home
function AuthAwareLanding() {
  const { user, loading } = useAuth();

  if (loading) return null;
  if (user) return <Navigate to="/home" replace />;

  return <LandingLayout><LandingPage /></LandingLayout>;
}

// New component for routes and GA tracking (inside Router)
function AppRoutes() {
  const location = useLocation();

  // Send page_view on route change
  useEffect(() => {
    if (window.gtag) {  // Ensure gtag is loaded
      window.gtag('event', 'page_view', {
        page_path: location.pathname + location.search,
        page_location: window.location.href,
        page_title: document.title,
      });
    }
  }, [location]);

  return (
    <Routes>
      {/* Landing page — auth users go to /home */}
      <Route path="/" element={<AuthAwareLanding />} />

      {/* Public routes with sidebar */}
      <Route path="/login" element={<Layout><LoginPage /></Layout>} />
      <Route path="/register" element={<Layout><RegisterPage /></Layout>} />
      <Route path="/auth/google/callback" element={<Layout><GoogleCallbackPage /></Layout>} />
      <Route path="/auth/fitbit/callback" element={<Layout><FitbitCallbackPage /></Layout>} />
      <Route path="/auth/:provider/callback" element={<Layout><DeviceCallbackPage /></Layout>} />
      <Route path="/about" element={<Layout><AboutPage /></Layout>} />
      <Route path="/contact" element={<Layout><ContactPage /></Layout>} />
      <Route path="/privacy-policy" element={<Layout><PrivacyPolicyPage /></Layout>} />
      <Route path="/terms-of-service" element={<Layout><TermsOfServicePage /></Layout>} />
      <Route path="/articles" element={<Layout><ArticlesListPage /></Layout>} />

      {/* New protected routes */}
      <Route
        path="/home"
        element={
          <Layout>
            <ProtectedRoute>
              <HomePage />
            </ProtectedRoute>
          </Layout>
        }
      />
      {/* The Digital Twin is now the "You" page. */}
      <Route
        path="/you"
        element={
          <Layout>
            <ProtectedRoute>
              <YouPage />
            </ProtectedRoute>
          </Layout>
        }
      />
      <Route path="/twin" element={<Navigate to="/you" replace />} />
      {/* Voice / conversational logging — the bottom-nav mic target. */}
      <Route
        path="/log"
        element={
          <Layout>
            <ProtectedRoute>
              <LogPage />
            </ProtectedRoute>
          </Layout>
        }
      />
      <Route
        path="/entries"
        element={
          <Layout>
            <ProtectedRoute>
              <EntriesPage />
            </ProtectedRoute>
          </Layout>
        }
      />
      <Route
        path="/data"
        element={
          <Layout>
            <ProtectedRoute>
              <DataPage />
            </ProtectedRoute>
          </Layout>
        }
      />
      <Route
        path="/insights"
        element={
          <Layout>
            <ProtectedRoute>
              <InsightsPage />
            </ProtectedRoute>
          </Layout>
        }
      />
      <Route
        path="/me"
        element={
          <Layout>
            <ProtectedRoute>
              <MePage />
            </ProtectedRoute>
          </Layout>
        }
      />
      <Route path="/guide" element={<Navigate to="/me" replace />} />

      {/* Legacy redirects for bookmark compatibility */}
      <Route path="/dashboard" element={<Navigate to="/home" replace />} />
      <Route path="/food-log" element={<Navigate to="/home" replace />} />
      <Route path="/fitbit-data" element={<Navigate to="/data" replace />} />
      <Route path="/profile" element={<Navigate to="/me" replace />} />
      <Route path="/personal-stats" element={<Navigate to="/me" replace />} />

      {/* Admin route - redirect to backend admin panel */}
      <Route path="/admin" element={<AdminRedirect />} />

      {/* Sitemap route - only in development mode */}
      {process.env.REACT_APP_DEVELOPMENT_MODE === 'True' && (
        <Route
          path="/sitemap.xml"
          element={
            <div className="p-4">
              The sitemap is served by the backend. Access it at{' '}
              <a href="http://localhost:8000/sitemap.xml" className="text-blue-500 hover:underline">
                http://localhost:8000/sitemap.xml
              </a>
            </div>
          }
        />
      )}

      {/* Public feature/landing pages - MUST be above the greedy article slug routes */}
      <Route path="/features/health-twin" element={<Layout><FeaturePage slug="health-twin" /></Layout>} />
      <Route path="/features/predictions" element={<Layout><FeaturePage slug="predictions" /></Layout>} />

      {/* Article detail routes - core articles (no category prefix) must come before category-prefixed routes */}
      <Route path="/:articleSlug" element={<Layout><ArticleDetailPage /></Layout>} />
      <Route path="/:categorySlug/:articleSlug" element={<Layout><ArticleDetailPage /></Layout>} />
    </Routes>
  );
}

function App() {
  useEffect(() => {
    // Load Google Analytics only in production mode
    if (process.env.REACT_APP_DEVELOPMENT_MODE !== 'False') {
      return;
    }

    const gaKey = process.env.REACT_APP_GOOGLE_ANALYTICS_KEY;
    if (!gaKey) {
      return;
    }

    // Load the gtag.js script
    const script = document.createElement('script');
    script.src = `https://www.googletagmanager.com/gtag/js?id=${gaKey}`;
    script.async = true;
    document.head.appendChild(script);

    // Initialize gtag
    const initScript = document.createElement('script');
    initScript.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', '${gaKey}');
    `;
    document.head.appendChild(initScript);
  }, []);

  return (
    <AuthProvider>
      <FitbitAutoSync />
      <Router>
        <AppRoutes />
      </Router>
    </AuthProvider>
  );
}

export default App;
